import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  Paper,
  Switch,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import PropTypes from 'prop-types';
import React from 'react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Add,
  Close,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import TaskDefinitionAutocomplete from '../../../../../../../components/Autocompletes/TaskDefinition';
import Horse from '../../../../../../../components/Horse';
import {
  addHorsePublicShare,
  deleteHorsesPublicShare,
  updateHorsesPublicShare,
} from '../../../../../../../redux/horsePublicShare/actions';
import { primary } from '../../../../../../../utils/brandColors';
import {
  fromPickerValue,
  toMomentValue,
} from '../../../../../../../utils/dateHelper';
import { NOTE_TYPE } from '../../../../../../../utils/enumHelper';
import theme from '../../../../../../../utils/theme';
import HorseNotesSelectPopover from '../HorseNoteSelectPopover';

const AddEditModal = ({
  open,
  onClose,
  horseId,
  horsePublicShare,
}) => {
  const dispatch = useDispatch();
  const fullscreen = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useColorScheme();

  // React state
  const [startDate, setStartDate] = React.useState(moment().startOf('day').toDate());
  const [startDateValidation, setStartDateValidation] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [endDateValidation, setEndDateValidation] = React.useState(null);
  const [shareNote, setShareNote] = React.useState('')
  const [sendEmail, setSendEmail] = React.useState(true)
  const [sharedNamesEmails, setSharedNamesEmails] = React.useState({})
  const [sharedNoteTypes, setSharedNoteTypes] = React.useState([])
  const [savingError, setSavingError] = React.useState(null);
  const [showSelectHorseNotes, setShowSelectHorseNotes] = React.useState(false);
  const [sharedNotes, setSharedNotes] = React.useState([])
  const [sharedTaskTypeIds, setSharedTaskTypeIds] = React.useState([])

  // Redux state
  const allEventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const isAddingHorsePublicShare = useSelector((state) => state.horsePublicShare.isAddingHorsePublicShare);
  const isEditingHorsePublicShare = useSelector((state) => state.horsePublicShare.isEditingHorsePublicShare);
  const isDeletingHorsePublicShare = useSelector((state) => state.horsePublicShare.isDeletingHorsePublicShare);
  const isLoading = isDeletingHorsePublicShare || isAddingHorsePublicShare || isEditingHorsePublicShare

  React.useEffect(() => {
    setStartDate(horsePublicShare
      ? new Date(horsePublicShare.startDate)
      : moment().startOf('day').toDate());
    setStartDateValidation(null);
    setEndDate((horsePublicShare && horsePublicShare.endDate)
      ? new Date(horsePublicShare.endDate)
      : null);
    setEndDateValidation(null);

    setSharedNamesEmails((horsePublicShare && horsePublicShare.sharedNamesEmails)
      ? horsePublicShare.sharedNamesEmails
      : {});

    setShareNote((horsePublicShare && horsePublicShare.shareNote)
      ? horsePublicShare.shareNote
      : '')

    setSharedNoteTypes((horsePublicShare && horsePublicShare.sharedNoteTypes)
      ? horsePublicShare.sharedNoteTypes
      : [])

    setSharedNotes((horsePublicShare && horsePublicShare.sharedNotes)
      ? horsePublicShare.sharedNotes
      : [])

    setSharedTaskTypeIds((horsePublicShare && horsePublicShare.sharedTaskTypeIds)
      ? horsePublicShare.sharedTaskTypeIds
      : []);

    setSavingError(null);
  }, [horsePublicShare, open]);

  const validateFields = () => {
    if (!startDate) {
      setStartDateValidation('Please provide a start date at this location.');
      return false;
    } setStartDateValidation(null);
    if (endDate && endDate.getTime() < startDate.getTime()) {
      setEndDateValidation('End date must be after start date.');
      return false;
    } setEndDateValidation(null);

    return true;
  };

  const handleSave = () => {
    if (!validateFields()) { return; }

    // Adding new location
    if (!horsePublicShare) {
      dispatch(addHorsePublicShare(horseId, {
        startDate,
        endDate,
        shareNote,
        sharedNamesEmails,
        sharedNoteTypes,
        sharedNoteIds: sharedNotes.map((note) => note.id),
        sharedTaskTypeIds,
      })).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to add the horse sharing link. Please contact Questri support.');
        console.error(error);
      });
    } else {
      dispatch(updateHorsesPublicShare(horsePublicShare.id, {
        startDate,
        endDate,
        shareNote,
        sharedNamesEmails,
        sharedNoteTypes,
        sharedNoteIds: sharedNotes.map((note) => note.id),
        sharedTaskTypeIds,
        sendEmail,
      })).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the horse sharing link. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const handleDelete = () => {
    dispatch(deleteHorsesPublicShare(horsePublicShare.id)).then(() => {
      onClose();
    }).catch((error) => {
      setSavingError(error.message || 'There was an error attempting to delete the horse sharing link. Please contact Questri support.');
      console.error(error);
    });
  };

  const handleSharedNotesChange = (event) => {
    if (sharedNoteTypes.find((t) => t === event.target.name)) {
      setSharedNoteTypes(sharedNoteTypes.filter((t) => t !== event.target.name))
    } else {
      setSharedNoteTypes([...sharedNoteTypes, event.target.name])
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          m: 0,
          mx: fullscreen
            ? 0
            : 2,
          width: '100%',
          maxWidth: 900,
        },
      }}
      aria-labelledby="modal-add-horse-share-title"
      aria-describedby="modal-add-horse--share-description"
    >
      <DialogTitle
        sx={{
          background: mode === 'light'
            ? primary.main
            : 'var(--template-palette-background-paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          id="modal-add-horse-share-title"
          variant="h4"
          component="span"
          color="#fff"
        >
          {horsePublicShare
            ? 'Edit'
            : 'Add'} Horse Sharing Link
        </Typography>
        <IconButton onClick={onClose}><Close sx={{ color: '#fff' }} /></IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ p: 3 }}
      >
        {horseId && barnHorsesMap[horseId] && (
          <Box sx={{ my: 2 }}><Horse horse={barnHorsesMap[horseId][0].horse} showName /></Box>
        )}
        <Grid
          container
          spacing={4}
          sx={{
            flex: 1,
            overflowY: 'auto',
          }}
        >
          <Grid size={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>Sharing Link Details</Typography>
              </Box>
              <TextField
                id="filled-multiline-flexible"
                label="Message/Note"
                fullWidth
                multiline
                rows={3}
                value={shareNote}
                onChange={(e) => setShareNote(e.target.value)}
                helperText="Anyone who views the horse with this link will see this note/message"
              />
            </Paper>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                height: '100%',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Sharing Start Date</Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Start Date"
                  value={toMomentValue(startDate)}
                  maxDate={toMomentValue(endDate || undefined)}
                  onChange={(newValue) => {
                    setStartDate(moment(fromPickerValue(newValue)).startOf('day').toDate());
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!startDateValidation,
                      helperText: startDateValidation,
                    },
                  }}
                />
              </LocalizationProvider>
            </Paper>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                height: '100%',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Sharing End Date</Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="End Date"
                  value={toMomentValue(endDate)}
                  minDate={toMomentValue(startDate)}
                  onChange={(newValue) => {
                    setEndDate(fromPickerValue(newValue));
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!endDateValidation,
                      helperText: endDateValidation
                        || 'After this date, the sharing link will no longer work. If left blank, access never ends.',
                    },
                  }}
                />
              </LocalizationProvider>
            </Paper>
          </Grid>
          <Grid size={12}>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >

              <Typography variant="subtitle1" fontWeight={600}>Share With Name/Emails</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSharedNamesEmails({
                    ...sharedNamesEmails,
                    [`UPDATE Name...${Object.keys(sharedNamesEmails).map(() => '.')}`]: '',
                  });
                }}
                startIcon={<Add />}
              >Add New Name/Email
              </Button>
            </Box>
            <Grid container>
              {
                Object.keys(sharedNamesEmails).map((num) => (
                  <Box
                    key={`name-number-${num}`}
                    component="form"
                    sx={{
                      '& .MuiTextField-root': {
                        m: 2,
                        ml: 0,
                      },
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Autocomplete
                      margin="normal"
                      freeSolo
                      options={[]}
                      variant="outlined"
                      id={`ref-num-name-${num}`}
                      size="small"
                      autoComplete
                      value={num}
                      onBlur={(e) => {
                        const deepCopy = JSON.parse(JSON.stringify(sharedNamesEmails)); // deep copy
                        const currentVal = JSON.parse(JSON.stringify(sharedNamesEmails[num])); // deep copy
                        // first remove old key
                        delete deepCopy[num];

                        setSharedNamesEmails({
                          ...deepCopy,
                          [e.target.value]: currentVal,
                        })
                      }}
                      label="Name"
                      sx={{
                        width: '23ch',
                        mr: 2,
                        ml: 0,
                      }}
                      renderInput={(params) => <TextField {...params} label="Name" />}
                    />
                    <TextField
                      margin="normal"
                      variant="outlined"
                      size="small"
                      id={`ref-num-val-${num}`}
                      value={sharedNamesEmails[num]}
                      onChange={(e) => setSharedNamesEmails({
                        ...sharedNamesEmails,
                        [num]: e.target.value,
                      })}
                      label="Email"
                      sx={{ width: '23ch ' }}
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        const deepCopy = JSON.parse(JSON.stringify(sharedNamesEmails)); // deep copy
                        delete deepCopy[num];
                        setSharedNamesEmails(deepCopy);
                      }}
                      startIcon={<Close color="danger" />}
                    >Remove
                    </Button>
                  </Box>
                ))
              }
            </Grid>
            <FormHelperText>Provide Name/Emails to share this horse link with.
              If you do not provide any emails, you can share the link to the horse profile instead.
            </FormHelperText>
          </Grid>
          <Grid size={12}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Horse Profile Details to Share</Typography>
            <FormHelperText>
              Horse profile fields (name, DOB, reference numbers, etc) along with horse feed
              is always available when sharing a horse. You can also decide what horse notes and tasks
              you would like show with this shared link.
            </FormHelperText>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
              <FormLabel component="legend">Share Notes by Note Type</FormLabel>
              <FormGroup>
                {
                  NOTE_TYPE.map((type) => (
                    <FormControlLabel
                      margin="none"
                      key={`note-type-checkbox-${type.enum}`}
                      sx={{ mb: 0 }}
                      control={(
                        <Checkbox
                          checked={!!(sharedNoteTypes && sharedNoteTypes.find((t) => t === type.enum))}
                          onChange={handleSharedNotesChange}
                          name={type.enum}
                        />
                      )}
                      label={`All ${type.label} Notes`}
                    />
                  ))
                }
              </FormGroup>
              <FormHelperText>All notes of the above selected types will be available at this shared link.</FormHelperText>
            </FormControl>
            <HorseNotesSelectPopover
              anchorEl={showSelectHorseNotes}
              open={!!showSelectHorseNotes}
              onClose={() => setShowSelectHorseNotes(null)}
              selectedHorseNotes={sharedNotes || []}
              onChangeSelectedHorseNotes={(horseNotes) => setSharedNotes(horseNotes)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
              <FormLabel component="legend">Share Specific Selected Notes</FormLabel>

              <Button size="small" sx={{ mb: 1 }} variant="outlined" onClick={(e) => setShowSelectHorseNotes(e.currentTarget)}>Select Specific Notes To Share</Button>
              <Chip label={`${sharedNotes.length} specific selected notes to be shared`} />
            </FormControl>
            <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
              <FormLabel component="legend">Share Task Records/History</FormLabel>
              <TaskDefinitionAutocomplete
                label="Share Task Records/History"
                value={allEventTemplates.filter((et) => !!sharedTaskTypeIds.find((t) => t === et.id))}
                eventTemplates={allEventTemplates}
                onSelect={(e, selected) => { setSharedTaskTypeIds(selected.map((et) => et.id)); }}
                multiple
                includeHorseTaskTypeTagId
                includeBarnTaskTypeTagId={false}
                includeEventTaskTypeTagId={false}
              />
              <FormHelperText>Select the type of tasks you would like to share in this shared profile.</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {!horsePublicShare && <Box />}
        {horsePublicShare && <Button onClick={handleDelete} endIcon={<DeleteIcon />} disabled={isLoading}>Delete</Button>}

        {horsePublicShare && (
          <FormControl size="small">
            <FormControlLabel
              control={(
                <Switch
                  checked={sendEmail}
                  onChange={(e) => {
                    setSendEmail(e.target.checked)
                  }}
                />
              )}
              label="Send Email To Shared With Emails"
            />
          </FormControl>
        )}
        {savingError && <Typography variant="caption" component="div" color="error" gutterBottom>{savingError}</Typography>}

        <Button onClick={handleSave} disabled={isLoading} variant="contained" color="primary" loading={isLoading} loadingPosition="start" startIcon={<SaveIcon />}>Save</Button>

      </DialogActions>
    </Dialog>
  );
};

AddEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  horseId: PropTypes.string,
  horsePublicShare: PropTypes.object,
};

AddEditModal.defaultProps = {
  horseId: null,
  horsePublicShare: null,
};

export default AddEditModal;
